/** @jsx jsx */
import { Dialog } from '@datacamp/waffles/dialog';
import { Paragraph } from '@datacamp/waffles/paragraph';
import { tokens } from '@datacamp/waffles/tokens';
import { jsx } from '@emotion/react';
import { addWeeks } from 'date-fns';
import React, { useEffect } from 'react';

import config from '../../config';
import { STORAGE_KEYS, useStorage } from '../../helpers/useStorage';
import { useDispatch, useSelector } from '../../interfaces/State';
import * as actions from '../../redux/actions';
import * as selectors from '../../redux/selectors';

const TopLeftBox1Style = {
  backgroundColor: tokens.colors.pink,
  width: 200,
  height: 18,
  left: 0,
  top: 0,
  position: 'absolute',
} as const;

const TopLeftBox2Style = {
  backgroundColor: tokens.colors.purple,
  width: 100,
  height: 18,
  left: 0,
  top: 18,
  position: 'absolute',
} as const;

const BottomRightBox1Style = {
  backgroundColor: tokens.colors.orange,
  width: 100,
  height: 18,
  right: 0,
  bottom: 18,
  position: 'absolute',
} as const;

const BottomRightBox2Style = {
  backgroundColor: tokens.colors.blue,
  width: 200,
  height: 18,
  right: 0,
  bottom: 0,
  position: 'absolute',
} as const;

type Props = Record<string, unknown>;

const ShowOrSkipRecap: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const userId = useSelector(selectors.selectUserSettings).get('id');
  const courseId = useSelector(selectors.selectCourse).get('id');
  const { setValue: setLearningRecapTmpDisabledUntil } = useStorage(
    STORAGE_KEYS.LEARNING_RECAP_TMP_DISABLED_UNTIL,
  );
  const {
    setValue: setLearningRecapSkipCount,
    value: skipLearningRecapCount,
  } = useStorage(STORAGE_KEYS.LEARNING_RECAP_SKIP_COUNT, 0);

  const { setValue: setLearningRecapEverSeen } = useStorage(
    STORAGE_KEYS.LEARNING_RECAP_EVER_SEEN,
    false,
  );

  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'heap_event',
        heap_event_name: 'Learn - Campus - Show Or Skip Recap Modal Shown',
        heap_event_properties: {
          app_id: config.appName,
          event_category: 'learning',
          identity: userId,
          course_id: courseId,
        },
      });
    }
  }, [courseId, userId]);

  return (
    <>
      <div css={TopLeftBox1Style} />
      <div css={TopLeftBox2Style} />
      <Dialog.Header css={{ paddingTop: tokens.spacing.xxlarge }}>
        Review what you learned to go faster today!
      </Dialog.Header>
      <Dialog.Body>
        <Paragraph>
          We have a recap of your previous learning activity
        </Paragraph>
      </Dialog.Body>
      <Dialog.Footer css={{ paddingBottom: tokens.spacing.xxlarge }}>
        <Dialog.Button
          data-cy="learn-recap-skip-button"
          variant="secondary"
          onClick={() => {
            const newSkipLearningRecapCount = skipLearningRecapCount + 1;
            setLearningRecapSkipCount(newSkipLearningRecapCount);
            if (newSkipLearningRecapCount % 2 === 0) {
              setLearningRecapTmpDisabledUntil(addWeeks(new Date(), 1));
            }
            dispatch(actions.removeModal());
          }}
        >
          Don't Show Now
        </Dialog.Button>
        <Dialog.Button
          data-cy="learn-recap-show-button"
          variant="primary"
          onClick={() => {
            setLearningRecapEverSeen(true);
            dispatch(
              actions.showModal({
                modal: selectors.MODAL_TYPE.SHOW_LEARN_RECAP,
              }),
            );
          }}
        >
          Show Recap
        </Dialog.Button>
      </Dialog.Footer>
      <div css={BottomRightBox1Style} />
      <div css={BottomRightBox2Style} />
    </>
  );
};

export default ShowOrSkipRecap;
